<template>
<Institutelayout>
    <div class="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-8 px-4">
      <div class="container mx-auto max-w-6xl">
        
        <!-- Form Section - Full Width -->
        <div class="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden mb-6">
          <div class="bg-blue-500 px-8 py-4">
            <h2 class="text-xl font-semibold text-white">Footer Configuration</h2>
          </div>
          
          <div class="p-6">
            <form @submit.prevent="saveChanges" class="space-y-6">
              <!-- Logo & Description Section -->
              <div class="bg-gray-50 rounded-xl p-4">
                <h3 class="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                  <svg class="w-5 h-5 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                  </svg>
                  Brand & Description
                </h3>
                
                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <!-- Logo Upload -->
                  <div>
                    <label class="block text-sm font-semibold text-gray-700 mb-2">
                      Footer Logo
                    </label>
                    <div class="relative">
                      <input 
                        type="file" 
                        id="footerLogo" 
                        @change="handleLogoUpload" 
                        accept="image/*"
                        class="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                      />
                      <div class="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-blue-400 transition-colors duration-200 bg-white">
                        <svg class="w-10 h-10 text-gray-400 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
                        </svg>
                        <p class="text-gray-600 font-medium text-sm">
                          {{ form.logoName || 'Click to upload logo' }}
                        </p>
                        <p class="text-xs text-gray-500 mt-1">PNG, JPG, SVG up to 5MB</p>
                      </div>
                    </div>
                  </div>
  
                  <!-- Description -->
                  <div>
                    <label for="description" class="block text-sm font-semibold text-gray-700 mb-2">
                      Description
                    </label>
                    <textarea 
                      id="description" 
                      v-model="form.description" 
                      rows="5"
                      class="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all duration-200 bg-white shadow-sm resize-none"
                      placeholder="A short description about your company or website."
                    ></textarea>
                  </div>
                </div>
              </div>
  
              <!-- Contact Information Section -->
              <div class="bg-gray-50 rounded-xl p-4">
                <h3 class="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                  <svg class="w-5 h-5 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.828 0L6.343 16.657m10.607-10.607L13.414 3.1a1.998 1.998 0 00-2.828 0L6.343 6.343m10.607 10.607H20m-4 0a4 4 0 11-8 0m8 0H4"></path>
                  </svg>
                  Contact Information
                </h3>
                
                <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label for="location" class="block text-sm font-semibold text-gray-700 mb-2">Location</label>
                    <input 
                      type="text" 
                      id="location" 
                      v-model="form.location" 
                      class="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all duration-200 bg-white shadow-sm"
                      placeholder="e.g., 123 Main St, City, Country"
                    />
                  </div>
                  <div>
                    <label for="mail" class="block text-sm font-semibold text-gray-700 mb-2">Email</label>
                    <input 
                      type="email" 
                      id="mail" 
                      v-model="form.mail" 
                      class="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all duration-200 bg-white shadow-sm"
                      placeholder="e.g., info@example.com"
                    />
                  </div>
                  <div>
                    <label for="phone" class="block text-sm font-semibold text-gray-700 mb-2">Phone Number</label>
                    <input 
                      type="tel" 
                      id="phone" 
                      v-model="form.phone" 
                      class="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all duration-200 bg-white shadow-sm"
                      placeholder="e.g., +1 (123) 456-7890"
                    />
                  </div>
                </div>
              </div>
  
              <!-- Useful Links Section -->
              <div class="bg-gray-50 rounded-xl p-4">
                <h3 class="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                  <svg class="w-5 h-5 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"></path>
                  </svg>
                  Useful Links
                </h3>
                
                <div class="space-y-3 max-h-48 overflow-y-auto">
                  <div v-for="(link, index) in form.usefulLinks" :key="index" class="flex items-center space-x-2 bg-white p-2 rounded border border-gray-200">
                    <input 
                      type="text" 
                      v-model="link.text" 
                      placeholder="Link Text" 
                      class="flex-1 px-2 py-1 text-sm border border-gray-200 rounded focus:ring-1 focus:ring-blue-500 outline-none"
                    />
                    <input 
                      type="url" 
                      v-model="link.url" 
                      placeholder="https://example.com" 
                      class="flex-1 px-2 py-1 text-sm border border-gray-200 rounded focus:ring-1 focus:ring-blue-500 outline-none"
                    />
                    <button 
                      type="button" 
                      @click="removeUsefulLink(index)" 
                      class="p-1 text-red-500 hover:text-red-700 rounded"
                    >
                      <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                      </svg>
                    </button>
                  </div>
                  <button 
                    type="button" 
                    @click="addUsefulLink" 
                    class="w-full py-1.5 px-2 border border-dashed border-gray-300 rounded text-gray-600 hover:border-blue-400 hover:text-blue-600 transition-colors duration-200 flex items-center justify-center space-x-1 text-sm"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                    </svg>
                    <span>Add Useful Link</span>
                  </button>
                </div>
              </div>
  
              <!-- Social Media Links Section -->
              <div class="bg-gray-50 rounded-xl p-4">
                <h3 class="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                  <svg class="w-5 h-5 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 10l-2 1m0 0l-2-1m2 1v2.5M14 10a2 2 0 10-4 0m4 0a2 2 0 11-4 0m0 10a2 2 0 104 0m0 0l-2-1m2 1v2.5M14 10l2 1m0 0l2-1m-2 1v2.5M14 10a2 2 0 10-4 0m4 0a2 2 0 11-4 0m0 10a2 2 0 104 0m0 0l-2-1m2 1v2.5M14 10l2 1m0 0l2-1m-2 1v2.5"></path>
                  </svg>
                  Social Media Links
                </h3>
                
                <div class="space-y-3 max-h-48 overflow-y-auto">
                  <div v-for="(social, index) in form.socialLinks" :key="index" class="flex items-center space-x-2 bg-white p-2 rounded border border-gray-200">
                    <select 
                      v-model="social.platform" 
                      class="flex-1 px-2 py-1 text-sm border border-gray-200 rounded focus:ring-1 focus:ring-blue-500 outline-none"
                    >
                      <option value="">Select Platform</option>
                      <option value="facebook">Facebook</option>
                      <option value="twitter">Twitter</option>
                      <option value="instagram">Instagram</option>
                      <option value="linkedin">LinkedIn</option>
                      <option value="youtube">YouTube</option>
                      <option value="github">GitHub</option>
                      <option value="tiktok">TikTok</option>
                    </select>
                    <input 
                      type="url" 
                      v-model="social.url" 
                      placeholder="Profile URL" 
                      class="flex-1 px-2 py-1 text-sm border border-gray-200 rounded focus:ring-1 focus:ring-blue-500 outline-none"
                    />
                    <button 
                      type="button" 
                      @click="removeSocialLink(index)" 
                      class="p-1 text-red-500 hover:text-red-700 rounded"
                    >
                      <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                      </svg>
                    </button>
                  </div>
                  <button 
                    type="button" 
                    @click="addSocialLink" 
                    class="w-full py-1.5 px-2 border border-dashed border-gray-300 rounded text-gray-600 hover:border-blue-400 hover:text-blue-600 transition-colors duration-200 flex items-center justify-center space-x-1 text-sm"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                    </svg>
                    <span>Add Social Link</span>
                  </button>
                </div>
              </div>
  
              <!-- Design Customization Section -->
              <div class="bg-gray-50 rounded-xl p-4">
                <h3 class="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                  <svg class="w-5 h-5 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zM21 5a2 2 0 00-2-2h-4a2 2 0 00-2 2v12a4 4 0 004 4h4a2 2 0 002-2V5z"></path>
                  </svg>
                  Design Customization
                </h3>
                
                <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <label for="footerBgColor" class="block text-sm font-semibold text-gray-700 mb-2">
                      Footer Background
                    </label>
                    <div class="flex items-center space-x-2">
                      <input 
                        type="color" 
                        id="footerBgColor" 
                        v-model="form.footerBgColor" 
                        class="w-10 h-10 border border-gray-200 rounded-lg cursor-pointer shadow-sm"
                      />
                      <span class="text-xs text-gray-600 font-mono">{{ form.footerBgColor }}</span>
                    </div>
                  </div>
  
                  <div>
                    <label for="textColor" class="block text-sm font-semibold text-gray-700 mb-2">
                      Text Color
                    </label>
                    <div class="flex items-center space-x-2">
                      <input 
                        type="color" 
                        id="textColor" 
                        v-model="form.textColor" 
                        class="w-10 h-10 border border-gray-200 rounded-lg cursor-pointer shadow-sm"
                      />
                      <span class="text-xs text-gray-600 font-mono">{{ form.textColor }}</span>
                    </div>
                  </div>
  
                  <div>
                    <label for="linkColor" class="block text-sm font-semibold text-gray-700 mb-2">
                      Link Color
                    </label>
                    <div class="flex items-center space-x-2">
                      <input 
                        type="color" 
                        id="linkColor" 
                        v-model="form.linkColor" 
                        class="w-10 h-10 border border-gray-200 rounded-lg cursor-pointer shadow-sm"
                      />
                      <span class="text-xs text-gray-600 font-mono">{{ form.linkColor }}</span>
                    </div>
                  </div>
  
                  <div>
                    <label for="fontFamily" class="block text-sm font-semibold text-gray-700 mb-2">
                      Font Family
                    </label>
                    <select 
                      id="fontFamily" 
                      v-model="form.fontFamily" 
                      class="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all duration-200 bg-white shadow-sm"
                    >
                      <option value="sans-serif">Sans-serif</option>
                      <option value="serif">Serif</option>
                      <option value="monospace">Monospace</option>
                      <option value="cursive">Cursive</option>
                      <option value="fantasy">Fantasy</option>
                    </select>
                  </div>
  
                  <div>
                    <label for="textSize" class="block text-sm font-semibold text-gray-700 mb-2">
                      Text Size
                    </label>
                    <select 
                      id="textSize" 
                      v-model="form.textSize" 
                      class="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all duration-200 bg-white shadow-sm"
                    >
                      <option value="text-xs">Extra Small</option>
                      <option value="text-sm">Small</option>
                      <option value="text-base">Medium</option>
                      <option value="text-lg">Large</option>
                    </select>
                  </div>
                </div>
              </div>
  
              <!-- Save Button -->
              <div class="flex justify-end pt-4 border-t border-gray-200">
                <button 
                  type="submit" 
                  class="px-6 py-3 bg-blue-500 text-white font-semibold rounded-xl hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-xl"
                >
                  <span class="flex items-center space-x-2">
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3-3m0 0l-3 3m3-3v12"></path>
                    </svg>
                    <span>Save Changes</span>
                  </span>
                </button>
              </div>
            </form>
          </div>
        </div>
  
        <!-- Preview Section - Below Form -->
        <div class="bg-white rounded-2xl shadow-xl border border-gray-100 hidden md:flex flex-col overflow-hidden">
          <div class="bg-blue-500 px-8 py-4">
            <h2 class="text-xl font-semibold text-white">Live Preview</h2>
          </div>
          
          <div class="p-6">
            <!-- Preview Footer -->
            <footer 
              class="py-8 px-6 rounded-xl shadow-lg"
              :style="{ 
                backgroundColor: form.footerBgColor,
                fontFamily: form.fontFamily,
                color: form.textColor
              }"
            >
              <div class="grid grid-cols-1 md:grid-cols-4 gap-8">
                <!-- Logo & Description Column -->
                <div class="flex flex-col items-start">
                  <div class="mb-4">
                    <img 
                      v-if="form.logoUrl" 
                      :src="form.logoUrl" 
                      alt="Footer Logo" 
                      class="h-12 object-contain"
                    />
                    <svg v-else class="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
                    </svg>
                  </div>
                  <p :class="form.textSize" class="leading-relaxed">
                    {{ form.description || 'Your company description goes here. We are committed to providing the best services.' }}
                  </p>
                </div>
  
                <!-- Contact Info Column -->
                <div>
                  <h4 class="font-bold mb-4 text-lg" :style="{ color: form.textColor }">Contact Us</h4>
                  <ul class="space-y-2" :class="form.textSize">
                    <li v-if="form.location" class="flex items-start">
                      <svg class="w-4 h-4 mr-2 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.828 0L6.343 16.657m10.607-10.607L13.414 3.1a1.998 1.998 0 00-2.828 0L6.343 6.343m10.607 10.607H20m-4 0a4 4 0 11-8 0m8 0H4"></path>
                      </svg>
                      <span>{{ form.location }}</span>
                    </li>
                    <li v-if="form.mail" class="flex items-center">
                      <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8m-1 12H4a2 2 0 01-2-2V6a2 2 0 012-2h16a2 2 0 012 2v12a2 2 0 01-2 2z"></path>
                      </svg>
                      <a :href="`mailto:${form.mail}`" :style="{ color: form.linkColor }" class="hover:underline">{{ form.mail }}</a>
                    </li>
                    <li v-if="form.phone" class="flex items-center">
                      <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.135a11.249 11.249 0 005.422 5.422l1.135-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path>
                      </svg>
                      <a :href="`tel:${form.phone}`" :style="{ color: form.linkColor }" class="hover:underline">{{ form.phone }}</a>
                    </li>
                  </ul>
                </div>
  
                <!-- Useful Links Column -->
                <div>
                  <h4 class="font-bold mb-4 text-lg" :style="{ color: form.textColor }">Useful Links</h4>
                  <ul class="space-y-2" :class="form.textSize">
                    <li v-for="(link, index) in form.usefulLinks" :key="index">
                      <a :href="link.url || '#'" :style="{ color: form.linkColor }" class="hover:underline">{{ link.text || `Link ${index + 1}` }}</a>
                    </li>
                  </ul>
                </div>
  
                <!-- Social Media Column -->
                <div>
                  <h4 class="font-bold mb-4 text-lg" :style="{ color: form.textColor }">Follow Us</h4>
                  <div class="flex space-x-4">
                    <a 
                      v-for="(social, index) in form.socialLinks" 
                      :key="index"
                      :href="social.url || '#'"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="text-2xl hover:opacity-80 transition-opacity duration-200"
                      :style="{ color: form.linkColor }"
                      :title="social.platform"
                    >
                      <!-- Placeholder for social media icons. In a real app, you'd use an icon library like Font Awesome or Lucide React/Vue. -->
                      <span v-if="social.platform === 'facebook'">&#x1F4F0;</span> <!-- Placeholder for Facebook -->
                      <span v-else-if="social.platform === 'twitter'">&#x1F426;</span> <!-- Placeholder for Twitter -->
                      <span v-else-if="social.platform === 'instagram'">&#x1F4F7;</span> <!-- Placeholder for Instagram -->
                      <span v-else-if="social.platform === 'linkedin'">&#x1F4BC;</span> <!-- Placeholder for LinkedIn -->
                      <span v-else-if="social.platform === 'youtube'">&#x1F3A5;</span> <!-- Placeholder for YouTube -->
                      <span v-else-if="social.platform === 'github'">&#x1F4BB;</span> <!-- Placeholder for GitHub -->
                      <span v-else-if="social.platform === 'tiktok'">&#x1F3A4;</span> <!-- Placeholder for TikTok -->
                      <span v-else>&#x2B50;</span> <!-- Generic placeholder -->
                    </a>
                  </div>
                </div>
              </div>
              <div class="text-center mt-8 pt-4 border-t border-gray-700/20" :class="form.textSize">
                &copy; {{ new Date().getFullYear() }} Your Company. All rights reserved.
              </div>
              <div class="flex justify-between items-center mt-4 pt-4 border-t border-gray-200">
                <div class="text-sm text-gray-500">
                  Preview updates in real-time as you make changes
                </div>
                <div class="flex space-x-3">
                  <button 
                    @click="deleteFooter"
                    class="px-4 py-2 bg-red-100 text-red-700 font-medium rounded-lg hover:bg-red-200 transition-colors duration-200 flex items-center space-x-2"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                    </svg>
                    <span>Delete</span>
                  </button>
                </div>
              </div>
            </footer>
          </div>
        </div>
      </div>
    </div>
</Institutelayout>
  </template>
  
  <script setup>
  import { ref } from 'vue';
import Institutelayout from '../institutelayout.vue';
  
  const form = ref({
    logo: null,
    logoName: '',
    logoUrl: '',
    description: '',
    location: '',
    mail: '',
    phone: '',
    usefulLinks: [],
    socialLinks: [],
    footerBgColor: '#1f2937', // Dark gray for footer background
    textColor: '#e5e7eb', // Light gray for text
    linkColor: '#93c5fd', // Light blue for links
    fontFamily: 'sans-serif',
    textSize: 'text-sm',
  });
  
  const handleLogoUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      form.value.logo = file;
      form.value.logoName = file.name;
      
      const reader = new FileReader();
      reader.onload = (e) => {
        form.value.logoUrl = e.target.result;
      };
      reader.readAsDataURL(file);
    } else {
      form.value.logo = null;
      form.value.logoName = '';
      form.value.logoUrl = '';
    }
  };
  
  const addUsefulLink = () => {
    form.value.usefulLinks.push({ text: '', url: '' });
  };
  
  const removeUsefulLink = (index) => {
    form.value.usefulLinks.splice(index, 1);
  };
  
  const addSocialLink = () => {
    form.value.socialLinks.push({ platform: '', url: '' });
  };
  
  const removeSocialLink = (index) => {
    form.value.socialLinks.splice(index, 1);
  };
  
  const getContrastColor = (hexColor) => {
    // Function to determine contrasting text color (black or white) for a given background color
    const r = parseInt(hexColor.slice(1, 3), 16);
    const g = parseInt(hexColor.slice(3, 5), 16);
    const b = parseInt(hexColor.slice(5, 7), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.5 ? '#000000' : '#ffffff';
  };
  
  const saveChanges = () => {
    console.log('Footer Data:', {
      ...form.value,
      logo: form.value.logo ? form.value.logo.name : null,
    });
    alert('Changes saved successfully! Check console for the submitted data.');
  };
  
  const editFooter = () => {
    alert('Edit functionality - This would allow you to modify the current footer configuration.');
  };
  
  const deleteFooter = () => {
    if (confirm('Are you sure you want to delete this footer configuration? This action cannot be undone.')) {
      form.value = {
        logo: null,
        logoName: '',
        logoUrl: '',
        description: '',
        location: '',
        mail: '',
        phone: '',
        usefulLinks: [],
        socialLinks: [],
        footerBgColor: '#1f2937',
        textColor: '#e5e7eb',
        linkColor: '#93c5fd',
        fontFamily: 'sans-serif',
        textSize: 'text-sm',
      };
      alert('Footer configuration deleted successfully!');
    }
  };
  </script>
  